/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useRef, useState, useEffect } from "react";
import { useTheme } from "../../theme/ThemeContext";
import envConfig from "../../config/envConfig"; // Holds fileServerUrl and fileServerApiKey
import { retrieve } from "../../utils/storage_helper";

/**
 * Uploads a previously-picked File object to the Flask storage server and
 * returns the URL string the form should persist. Exported so the form's
 * submit handler can defer the actual upload until the row is being saved.
 *
 * Throws on validation/network failure. Caller is responsible for any
 * orchestration (parallel/sequential, cleanup on partial failure).
 */
export async function uploadPendingFile(file, {
  is_public = true,
  fileType = "other",
  apiHeaders = {},
  // Opt-in, and off by default so no existing caller changes behaviour.
  //
  // The branch below hands non-images a Django-proxied URL that requires an
  // OAuth token. That is right for a private document, and wrong for anything a
  // PUBLIC page links to — a participant downloading a route file has no token,
  // so the link 401s. The storage server already returns a directly-servable
  // public_url for ANY type when public=true; this just stops ignoring it.
  preferPublicUrl = false,
} = {}) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("file_type", fileType);
  if (is_public) formData.append("public", "true");

  const headers = { ...apiHeaders };
  headers["X-API-KEY"] = envConfig.fileServerApiKey;
  headers["X-ORG-ID"] = envConfig.organizationId;
  headers["X-APP-ID"] = envConfig.applicationId;

  const uploadUrl = envConfig.fileServerUrl.replace(/\/$/, "") + "/upload";
  const response = await fetch(uploadUrl, {
    method: "POST",
    headers,
    body: formData,
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Upload failed");
  }

  const fileId = data.file_id
    || (data.public_url ? data.public_url.split("/").pop().split("?")[0] : "");

  // Images stored as direct public URL so <img> tags render anywhere.
  // Documents stored as Django-proxied URL that requires OAuth.
  let fullUrl = "";
  const isImage = (fileType === "image") || (file.type || "").startsWith("image/");
  if ((isImage || preferPublicUrl) && data.public_url) {
    fullUrl = envConfig.fileServerUrl.replace(/\/$/, "") + data.public_url;
  } else if (fileId) {
    const apiBase = envConfig.backendApiUrl ? envConfig.backendApiUrl.replace(/\/$/, "") : "";
    fullUrl = `${apiBase}/arcee/application/file/download/${fileId}/?filename=${encodeURIComponent(file.name)}`;
  }
  return { url: fullUrl, fileId, fileType };
}

/**
 * Best-effort delete of a previously-uploaded file. Used by the form to
 * clean up orphans when a save fails after upload, or when a stored file
 * is being replaced/removed.
 *
 * Swallows errors (returns true/false) — partial cleanup is fine, the
 * orphan can be reclaimed by a separate sweep later if it persists.
 */
export async function deleteUploadedFile(fileIdOrUrl) {
  if (!fileIdOrUrl) return false;
  // Accept either a raw file_id or any URL containing one
  const uuidMatch = String(fileIdOrUrl).match(
    /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/
  );
  if (!uuidMatch) return false;
  const fileId = uuidMatch[0];

  const headers = {
    "X-API-KEY": envConfig.fileServerApiKey,
    "X-ORG-ID": envConfig.organizationId,
    "X-APP-ID": envConfig.applicationId,
  };
  try {
    const resp = await fetch(
      envConfig.fileServerUrl.replace(/\/$/, "") + `/files/${fileId}`,
      { method: "DELETE", headers }
    );
    return resp.ok || resp.status === 204;
  } catch {
    return false;
  }
}

/*
 * Usage Example:
 * <FileUploadInput
 *   is_public={true}
 *   value={form.banner_image}
 *   onChange={url => setForm(f => ({ ...f, banner_image: url }))}
 *   allowedFileTypes={["image"]}
 *   expectedImageWidth={1200}
 *   expectedImageHeight={400}
 *   inputProps={{ name: "banner_image" }}
 *   hint="Only .png, .jpg. Max 1 MB. Size must be exactly 1200x400."
 * />
 */

// --- File type mapping & validation setup ---
export const FILE_TYPE_MAP = {
    image: {
        label: "Image",
        accept: "image/*",
        maxSize: 1 * 1024 * 1024,
        validate: (file) => file.type.startsWith("image/"),
    },
    video: {
        label: "Video",
        accept: "video/*",
        maxSize: 5 * 1024 * 1024,
        validate: (file) => file.type.startsWith("video/"),
    },
    audio: {
        label: "Audio",
        accept: "audio/*",
        maxSize: 2 * 1024 * 1024,
        validate: (file) => file.type.startsWith("audio/"),
    },
    text: {
        label: "Text",
        accept: ".txt,.csv,.log,.json,.xml,.yaml,.md",
        maxSize: 0.5 * 1024 * 1024,
        validate: (file) =>
            file.type.startsWith("text/") ||
            [".txt", ".csv", ".log", ".json", ".xml", ".yaml", ".md"].some(ext =>
                file.name.endsWith(ext)
            ),
    },
    document: {
        label: "Document",
        accept: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp",
        maxSize: 5 * 1024 * 1024,
        validate: (file) =>
            [
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "application/vnd.ms-excel",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                "application/vnd.ms-powerpoint",
                "application/vnd.openxmlformats-officedocument.presentationml.presentation",
            ].includes(file.type) ||
            [
                ".pdf",
                ".doc",
                ".docx",
                ".xls",
                ".xlsx",
                ".ppt",
                ".pptx",
                ".odt",
                ".ods",
                ".odp",
            ].some(ext => file.name.endsWith(ext)),
    },
    archive: {
        label: "Archive",
        accept: ".zip,.rar,.tar,.gz,.tgz,.7z,.bz2",
        maxSize: 4 * 1024 * 1024,
        validate: (file) =>
            [".zip", ".rar", ".tar", ".gz", ".tgz", ".7z", ".bz2"].some(ext =>
                file.name.endsWith(ext)
            ),
    },
    // Route files for watches. Its own type rather than "other" because "other"
    // accepts anything at 1 MB, and a dense GPX track easily exceeds that while
    // still being a perfectly ordinary course.
    gpx: {
        label: "GPX route",
        accept: ".gpx,application/gpx+xml",
        maxSize: 5 * 1024 * 1024,
        validate: (file) =>
            file.name.toLowerCase().endsWith(".gpx") ||
            ["application/gpx+xml", "application/xml", "text/xml"].includes(file.type),
    },
    other: {
        label: "Other",
        accept: "*",
        maxSize: 1 * 1024 * 1024,
        validate: () => true,
    },
};

// --- Simple SVG Upload Icon ---
function UploadIcon({ size = 22, color = "#2d6cf6" }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <title>Upload</title>
            <rect x="5" y="16" width="14" height="3" rx="1.5" fill={color} opacity={0.7} />
            <rect x="11" y="8" width="2" height="6" rx="1" fill={color} />
            <polygon points="12,4 8,9 10,9 10,13 14,13 14,9 16,9" fill={color} />
        </svg>
    );
}

/**
 * Helper: read an image file and resolve its natural width/height.
 */
function getImageDimensions(file) {
    return new Promise((resolve, reject) => {
        try {
            const reader = new FileReader();
            reader.onload = evt => {
                const img = new Image();
                img.onload = () => {
                    resolve({ width: img.width, height: img.height });
                };
                img.onerror = reject;
                img.src = evt.target.result;
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        } catch (e) {
            reject(e);
        }
    });
}

// --- Main Component ---
//
// Two-phase upload flow:
//   1. User picks a file → component validates it locally, stores the File
//      in `pendingFile` state, reports it to parent via `onPendingFile`.
//      No network call to Flask is made yet — `value` (the persisted URL)
//      is left unchanged so the form can fall back to the existing file
//      if the user cancels.
//   2. When the form's Submit fires, the parent uploads any pending files
//      via `uploadPendingFile()` (exported above) and writes the returned
//      URL into the record. The Copy Link button stays disabled until then.
//
// Existing-file case (update mode): when `value` is a URL string and no
// `pendingFile` exists, behaviour is identical to before the refactor.
export default function FileUploadInput({
    is_public,
    value,
    onChange,
    onPendingFile,    // (file|null, fieldName)  — called whenever the user picks/clears a pending upload
    pendingFile,      // File object lifted from parent state (or null)
    allowedFileTypes = ["image", "video", "audio", "text", "document", "archive", "other"],
    apiHeaders = {},
    error,             // external error message (optional)
    inputProps = {},   // props passed down to readonly URL input
    uploadTooltip = "Select and upload file",
    hint,              // help/hint text below control
    expectedImageWidth,   // optional: enforce image width
    expectedImageHeight,   // optional: enforce image height
    preferPublicUrl,       // optional: see uploadPendingFile — public URL for non-images
    placeholder            // optional: override default placeholder
}) {
    const { theme } = useTheme();
    const fileRef = useRef();

    const [errorMsg, setErrorMsg] = useState("");
    const [fileTypeDetected, setFileTypeDetected] = useState(null);
    const [copied, setCopied] = useState(false);

    // Local-only preview URL (Blob URL) for the pending File. We create it
    // on file pick and revoke it when the file changes or component unmounts
    // so we don't leak memory.
    const [previewUrl, setPreviewUrl] = useState(null);
    useEffect(() => {
        if (pendingFile && pendingFile.type && pendingFile.type.startsWith("image/")) {
            const url = URL.createObjectURL(pendingFile);
            setPreviewUrl(url);
            return () => URL.revokeObjectURL(url);
        }
        setPreviewUrl(null);
        return undefined;
    }, [pendingFile]);

    // Build accept attribute list from allowed file types
    const acceptList = allowedFileTypes
        .map(type => FILE_TYPE_MAP[type])
        .filter(Boolean)
        .map(f => f.accept)
        .join(",");

    // Generate default placeholder text based on allowed types
    const defaultPlaceholder = allowedFileTypes
        .map(type => {
            const info = FILE_TYPE_MAP[type];
            return info ? `${info.label} (Max ${(info.maxSize / (1024 * 1024)).toFixed(1)} MB)` : null;
        })
        .filter(Boolean)
        .join("; ");

    const finalPlaceholder = placeholder || `Allowed: ${defaultPlaceholder}`;

    /**
     * Validate file type and size.
     * Returns string error or null if OK.
     */
    const basicValidateFile = (file) => {
        for (let type of allowedFileTypes) {
            const typeInfo = FILE_TYPE_MAP[type];
            if (typeInfo.validate(file)) {
                setFileTypeDetected(type);
                if (file.size > typeInfo.maxSize) {
                    return `File too large for ${typeInfo.label} (max ${(typeInfo.maxSize / 1024 / 1024).toFixed(2)} MB).`;
                }
                return null;
            }
        }
        return "Invalid file type.";
    };

    /**
     * Trigger hidden file input click when Upload button is pressed.
     */
    const handleUploadClick = () => {
        if (fileRef.current) fileRef.current.value = "";
        fileRef.current?.click();
    };

    /**
     * Handle file selection:
     * - Basic type/size validation
     * - Optional image width/height validation
     * - Upload to server if all good
     */
    /**
     * Handle file selection — validates locally and lifts the File object to
     * the parent. The actual upload to Flask is deferred until the form's
     * Submit handler runs, so abandoned forms never create orphan blobs.
     */
    const handleFileChange = async (e) => {
        setErrorMsg("");
        setFileTypeDetected(null);

        const file = e.target.files[0];
        if (!file) return;

        // Step 1: basic validation (type/size)
        const validationError = basicValidateFile(file);
        if (validationError) {
            setErrorMsg(validationError);
            return;
        }

        // Step 2: optional image dimension check (only when constraints supplied)
        if (
            file.type.startsWith("image/") &&
            (expectedImageWidth || expectedImageHeight)
        ) {
            try {
                const { width, height } = await getImageDimensions(file);
                if (
                    (expectedImageWidth && width !== expectedImageWidth) ||
                    (expectedImageHeight && height !== expectedImageHeight)
                ) {
                    const msgParts = [];
                    if (expectedImageWidth && expectedImageHeight) {
                        msgParts.push(`Expected ${expectedImageWidth}x${expectedImageHeight}px`);
                    } else if (expectedImageWidth) {
                        msgParts.push(`Expected width ${expectedImageWidth}px`);
                    } else if (expectedImageHeight) {
                        msgParts.push(`Expected height ${expectedImageHeight}px`);
                    }
                    msgParts.push(`but got ${width}x${height}px.`);
                    setErrorMsg(msgParts.join(" "));
                    return;
                }
            } catch (dimErr) {
                setErrorMsg("Could not read image dimensions.");
                return;
            }
        }

        // Step 3: lift the File to the parent (deferred upload).
        if (onPendingFile) {
            onPendingFile(file, inputProps.name);
        }
        // We intentionally do NOT call onChange here — value still holds the
        // previously-persisted URL (if any) until the parent's submit
        // replaces it with a fresh one. That way, cancelling the form leaves
        // the existing record intact and Flask completely untouched.
    };

    /**
     * Copy URL to clipboard with fallback. Disabled while a pending file
     * exists — there is no URL to copy until the form is submitted.
     */
    const handleCopy = async () => {
        if (!value || pendingFile) return;
        try {
            await navigator.clipboard.writeText(value);
        } catch {
            const input = document.createElement("textarea");
            input.value = value;
            document.body.appendChild(input);
            input.select();
            document.execCommand("copy");
            document.body.removeChild(input);
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    /**
     * Clear: drops either the pending File (without touching Flask) or the
     * stored URL (which will be cleaned up on submit if the form persists
     * the new empty value).
     */
    const handleClear = () => {
        if (pendingFile && onPendingFile) {
            onPendingFile(null, inputProps.name);
            return;
        }
        if (onChange) onChange("", inputProps.name);
    };

    // Final error to show (internal > external)
    const finalError = errorMsg || error;

    return (
        <div style={{ width: "100%" }}>
            {/* Main outlined control (URL + actions) */}
            <div
                style={{
                    border: `1.5px solid ${theme.primary}`,
                    borderRadius: theme.buttonRadius,
                    display: "flex",
                    alignItems: "center",
                    padding: "4px",
                    background: "#fff"
                }}
            >
                {/* Read-only URL — or pending filename if a file is queued */}
                <input
                    {...inputProps}
                    readOnly
                    value={
                        pendingFile
                            ? `${pendingFile.name}  ·  will be uploaded on Save`
                            : (value || "")
                    }
                    placeholder={finalPlaceholder}
                    style={{
                        border: "none",
                        background: "none",
                        color: pendingFile ? theme.primary : "#222",
                        fontStyle: pendingFile ? "italic" : "normal",
                        fontFamily: "inherit",
                        width: "100%",
                        padding: "9px 10px",
                        outline: "none",
                        fontSize: "1em"
                    }}
                    className={inputProps.className}
                />

                {/* Action buttons */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "3px",
                        marginLeft: "6px"
                    }}
                >
                    {/* Upload (open file picker) */}
                    <button
                        style={{
                            border: `1.5px solid ${theme.primary}`,
                            borderRadius: theme.buttonRadius,
                            padding: "2px 7px",
                            background: "none",
                            color: theme.primary,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center"
                        }}
                        type="button"
                        title={uploadTooltip}
                        onClick={handleUploadClick}
                    >
                        <UploadIcon size={22} color={theme.primary} />
                    </button>

                    {/* Copy & Clear — visible when there is either a stored URL or a pending file. */}
                    {(value || pendingFile) && (
                        <>
                            {/* Copy is disabled while a pending file exists: there is no URL yet. */}
                            <button
                                aria-disabled={!value || !!pendingFile}
                                style={{
                                    border: `1.5px solid ${(!value || pendingFile) ? "#cbd5e1" : theme.primary}`,
                                    borderRadius: theme.buttonRadius,
                                    padding: "2px 8px",
                                    background: "none",
                                    color: (!value || pendingFile) ? "#cbd5e1" : theme.primary,
                                    cursor: (!value || pendingFile) ? "not-allowed" : "pointer",
                                    position: "relative",
                                    opacity: (!value || pendingFile) ? 0.7 : 1
                                }}
                                type="button"
                                title={
                                    pendingFile
                                        ? "Save the record first to get a copyable link"
                                        : (copied ? "Copied!" : "Copy URL to clipboard")
                                }
                                onClick={
                                    (!value || pendingFile)
                                        ? (e) => e.preventDefault()
                                        : handleCopy
                                }
                            >
                                {copied ? "Copied!" : "📋"}
                            </button>
                            <button
                                style={{
                                    border: `1.5px solid ${theme.primary}`,
                                    borderRadius: theme.buttonRadius,
                                    padding: "2px 8px",
                                    background: "none",
                                    color: theme.primary,
                                    cursor: "pointer"
                                }}
                                type="button"
                                title={pendingFile ? "Discard the pending file" : "Clear URL"}
                                onClick={handleClear}
                            >
                                ✖
                            </button>
                        </>
                    )}

                    {/* Hidden file input */}
                    <input
                        type="file"
                        ref={fileRef}
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                        accept={acceptList || "*"}
                    />
                </div>

                {/* Local spinner keyframes */}
                <style>
                    {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
                </style>
            </div>

            {/* Hint under control */}
            {hint && (
                <div
                    style={{
                        fontSize: ".96em",
                        color: "#545d6f",
                        margin: "3px 2px 0 5px"
                    }}
                >
                    {hint}
                </div>
            )}

            {/* Pending-upload notice (and local image preview if applicable) */}
            {pendingFile && (
                <div
                    style={{
                        marginTop: 6,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: ".92em",
                        color: "#0f5132",
                        background: "#d1e7dd",
                        border: "1px solid #badbcc",
                        borderRadius: theme.buttonRadius,
                        padding: "6px 10px",
                    }}
                >
                    {previewUrl && (
                        <img
                            src={previewUrl}
                            alt={pendingFile.name}
                            style={{
                                width: 36, height: 36,
                                objectFit: "cover",
                                borderRadius: 4,
                                flexShrink: 0,
                            }}
                        />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {pendingFile.name}
                        </div>
                        <div style={{ fontSize: ".85em", opacity: 0.85 }}>
                            {(pendingFile.size / 1024).toFixed(1)} KB · uploads when you Save
                        </div>
                    </div>
                </div>
            )}

            {/* Error under hint */}
            {finalError && (
                <div
                    style={{
                        color: "#d53b3b",
                        fontSize: "0.92em",
                        marginTop: "4px",
                        marginLeft: "5px"
                    }}
                >
                    {finalError}
                </div>
            )}
        </div>
    );
}
